import { createFileRoute, Link, useRouter } from "@tanstack/react-router";

import {
  EXTERNAL_DISCLAIMER,
  eventArtwork,
  eventArtworkAlt,
  eventPlace,
  eventReserveLabel,
  eventBookedMessage,
} from "@/lib/event-presentation";

import { ExternalLink, MapPin } from "lucide-react";
import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  ArrowLeft,
  CalendarDays,
  Clock,
  Ticket,
  
  MessageCircle,
  Send,
  Loader2,
  Users,
} from "lucide-react";
import {

  formatEventDualTime,
  detectTimeZone,
  getCategory,
  getDiscussion,
  postDiscussion,
  type DiscussionMsg,
} from "@/lib/events";
import type { PublicEvent } from "@/lib/events.functions";
import {
  getPublicEvent,
  getEventForMember,
  listMyReservations,
  reserveEvent,
  cancelReservation,
  canIReserveEvents,
} from "@/lib/events.functions";


import { useMembership } from "@/hooks/useMembership";
import { EventFeedback } from "@/components/EventFeedback";
import { ComplianceNotice } from "@/components/ComplianceNotice";
import { EventTimeZoneCard } from "@/components/events/EventTimeZoneCard";
import { WorkshopJoinPanel } from "@/components/events/WorkshopJoinPanel";

import { Lock } from "lucide-react";
import { toast } from "sonner";
import { notifyActivityChange } from "@/lib/activity-events";
import { startMembershipJourney, MEMBERSHIP_JOURNEY_PATH } from "@/lib/funnel";

export const Route = createFileRoute("/events_/$slug")({
  loader: async ({ params }) => {
    try {
      return await getPublicEvent({ data: { slug: params.slug } });
    } catch {
      return null;
    }
  },
  head: ({ params, loaderData }) => {
    const url = `https://recoverytrauma.com/events/${params.slug}`;
    const ev = loaderData;
    const title = ev ? `${ev.title} | Recovery Trauma™ Event` : "Event | Recovery Trauma™";
    const description = ev
      ? (ev.description ?? "").replace(/\s+/g, " ").trim().slice(0, 155) ||
        `Live trauma-informed event hosted by ${ev.host}.`
      : "Live trauma-informed events from Recovery Trauma™.";
    const image = ev?.event_image_url ?? undefined;
    const meta: Array<Record<string, string>> = [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:url", content: url },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: image ? "summary_large_image" : "summary" },
    ];
    if (image) {
      meta.push({ property: "og:image", content: image });
      meta.push({ name: "twitter:image", content: image });
    }
    const scripts = ev
      ? [
          {
            type: "application/ld+json",
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Event",
              name: ev.title,
              description,
              startDate: ev.starts_at,
              endDate: new Date(
                new Date(ev.starts_at).getTime() + (ev.duration_minutes ?? 60) * 60_000,
              ).toISOString(),
              eventAttendanceMode:
                ev.location && ev.location.trim().length > 0
                  ? "https://schema.org/OfflineEventAttendanceMode"
                  : "https://schema.org/OnlineEventAttendanceMode",
              eventStatus: "https://schema.org/EventScheduled",
              image: image ?? undefined,
              location:
                ev.location && ev.location.trim().length > 0
                  ? { "@type": "Place", name: ev.location, address: ev.location }
                  : { "@type": "VirtualLocation", url },
              organizer: {
                "@type": "Organization",
                name: "Recovery Trauma™",
                url: "https://recoverytrauma.com",
              },
              performer: { "@type": "Person", name: ev.host },
              url,
            }),
          },
          {
            type: "application/ld+json",
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: "https://recoverytrauma.com" },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: "Events",
                  item: "https://recoverytrauma.com/events",
                },
                { "@type": "ListItem", position: 3, name: ev.title, item: url },
              ],
            }),
          },
        ]
      : [];
    return {
      meta,
      links: [{ rel: "canonical", href: url }],
      scripts,
    };
  },

  component: EventDetailRouter,
});

function EventDetailRouter() {
  const { slug } = Route.useParams();
  const seed = Route.useLoaderData();
  const getPublic = useServerFn(getPublicEvent);
  const publicQuery = useQuery({
    queryKey: ["event-public", slug],
    queryFn: () => getPublic({ data: { slug } }),
    // Seeded from the loader so the event title renders server-side (crawlers
    // and social scrapers see the real H1 instead of a spinner).
    initialData: seed ?? undefined,
  });


  if (publicQuery.isLoading) {
    return (
      <div className="mx-auto flex max-w-3xl items-center gap-2 px-4 py-16 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading event…
      </div>
    );
  }

  const ev = publicQuery.data;
  if (!ev) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-16 text-center">
        <p className="font-display text-2xl">Event not found</p>
        <Link to="/events" className="btn-gold-outline mt-4 inline-flex text-sm">
          Back to events
        </Link>
      </div>
    );
  }

  if (ev.source === "external") {
    return <ExternalEventDetail event={ev} />;
  }

  return <EventDetailPage seed={ev} />;
}

function ExternalEventDetail({ event }: { event: import("@/lib/events.functions").PublicEvent }) {
  const router = useRouter();
  const cat = getCategory(event.category);
  return (
    <div className="mx-auto max-w-3xl px-4 py-10 pb-28 md:py-14 md:pb-14">
      <button
        type="button"
        onClick={() => router.history.back()}
        className="mb-6 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1 text-[11px] font-semibold text-foreground">
          <ExternalLink className="h-3 w-3" />
          External Event
        </span>
        <span className={`inline-flex items-center gap-1 rounded-full bg-gradient-to-br px-3 py-1 text-xs font-semibold ${cat.hue}`}>
          {cat.emoji} {cat.label}
        </span>
      </div>

      <h1 className="mt-4 font-display text-3xl text-foreground md:text-5xl">{event.title}</h1>

      <EventTimeZoneCard startsAt={event.starts_at} className="mt-4" />

      <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <Clock className="h-4 w-4" /> {event.duration_minutes} min
        </span>
        <span className="inline-flex items-center gap-1.5">
          <MapPin className="h-4 w-4" /> {eventPlace(event)}
        </span>
        {event.host ? <span>Organised by {event.host}</span> : null}
      </div>

      <div className="mt-6 overflow-hidden rounded-2xl border border-gold/30 bg-gradient-to-br from-cream/70 to-background">
        <img
          loading="lazy"
          decoding="async"
          src={eventArtwork(event)}
          alt={eventArtworkAlt(event)}
          width={1280}
          height={800}
          className="h-36 w-full object-cover object-[50%_38%] sm:h-48 md:h-56"
        />
      </div>


      {event.description ? (
        <p className="mt-6 whitespace-pre-wrap text-base leading-relaxed text-foreground">
          {event.description}
        </p>
      ) : null}

      <section className="mt-8 rounded-2xl border border-border bg-muted/40 p-6">
        <p className="font-display text-lg text-foreground">
          Organised independently
        </p>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
          {EXTERNAL_DISCLAIMER}
        </p>
        {event.external_url ? (
          <a
            href={event.external_url}
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="btn-gold mt-4 inline-flex items-center gap-1.5 text-sm"
          >
            View External Event <ExternalLink className="h-3.5 w-3.5" />
          </a>
        ) : null}
      </section>


      <div className="mt-8">
        <ComplianceNotice variant="events" />
      </div>
    </div>
  );
}

function EventDetailPage({ seed }: { seed: PublicEvent }) {
  const { slug } = Route.useParams();
  const router = useRouter();
  const { isAuthed, isMember, loading: authLoading } = useMembership();
  const queryClient = useQueryClient();

  const getPublic = useServerFn(getPublicEvent);
  const getMember = useServerFn(getEventForMember);
  const fetchReservations = useServerFn(listMyReservations);
  const reserveFn = useServerFn(reserveEvent);
  const cancelFn = useServerFn(cancelReservation);
  const checkAccess = useServerFn(canIReserveEvents);

  // Server-authoritative membership check: covers Stripe, PayPal,
  // complimentary/founding memberships and admins.
  const accessQuery = useQuery({
    queryKey: ["event-access"],
    queryFn: () => checkAccess(),
    enabled: isAuthed,
    staleTime: 60_000,
  });
  const canReserve = isMember || accessQuery.data === true;

  const eventQuery = useQuery({
    queryKey: ["event", slug, canReserve ? "member" : "public"],
    queryFn: () =>
      canReserve ? getMember({ data: { slug } }) : getPublic({ data: { slug } }),
    enabled: !authLoading && !(isAuthed && accessQuery.isLoading),
  });

  const reservationsQuery = useQuery({
    queryKey: ["my-reservations"],
    queryFn: () => fetchReservations(),
    enabled: canReserve,
  });

  const event = eventQuery.data;
  const reserved = !!(event && (reservationsQuery.data ?? []).includes(event.id));


  const reserveMutation = useMutation({
    mutationFn: () =>
      reserved
        ? cancelFn({ data: { eventId: event!.id } })
        : reserveFn({ data: { eventId: event!.id, timeZone: detectTimeZone() } }),
    onSuccess: () => {
      if (reserved) {
        toast.success("Reservation cancelled");
      } else {
        const booked = eventBookedMessage(slug);
        toast.success(booked.title, {
          description:
            booked.note ??
            (event
              ? formatEventDualTime(event.starts_at, detectTimeZone())
              : undefined),
        });
      }
      queryClient.invalidateQueries({ queryKey: ["my-reservations"] });
      queryClient.invalidateQueries({ queryKey: ["event", slug] });
      queryClient.invalidateQueries({ queryKey: ["events"] });
      notifyActivityChange("event");
    },

    onError: (e: Error) => toast.error(e.message),
  });

  if (eventQuery.isLoading || authLoading) {
    // The loader already resolved the public event, so render its title as the
    // page H1 while member-specific details load (crawlers see real content).
    return (
      <div className="mx-auto max-w-3xl px-4 py-16">
        <h1 className="font-display text-3xl text-foreground md:text-5xl">{seed.title}</h1>
        <p className="mt-4 inline-flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading event…
        </p>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-16 text-center">
        <p className="font-display text-2xl">Event not found</p>
        <Link to="/events" className="btn-gold-outline mt-4 inline-flex text-sm">
          Back to events
        </Link>
      </div>
    );
  }

  // Public visitors may read the whole event page — booking is what's gated.
  // (The pre-launch lock intentionally does NOT apply here.)

  const cat = getCategory(event.category);
  const evAny = event as { zoom_url?: string | null; zoom_passcode?: string | null };
  const zoomUrl: string | null = evAny.zoom_url ?? null;
  const zoomPasscode: string | null = evAny.zoom_passcode ?? null;
  // Once a session has finished it can no longer be booked.
  const eventEndsAt =
    new Date(event.starts_at).getTime() + (event.duration_minutes ?? 60) * 60_000;
  const isPastEvent = Number.isFinite(eventEndsAt) && eventEndsAt <= Date.now();


  return (
    <div className="mx-auto max-w-3xl px-4 py-10 pb-28 md:py-14 md:pb-14">
      <button
        type="button"
        onClick={() => router.history.back()}
        className="mb-6 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--gradient-gold)] px-3 py-1 text-[11px] font-semibold text-foreground ring-1 ring-gold/50">
          Hosted by Recovery Trauma™
        </span>
        <span className={`inline-flex items-center gap-1 rounded-full bg-gradient-to-br px-3 py-1 text-xs font-semibold ${cat.hue}`}>
          {cat.emoji} {cat.label}
        </span>
      </div>

      <h1 className="mt-4 font-display text-3xl text-foreground md:text-5xl">{event.title}</h1>

      <EventTimeZoneCard startsAt={event.starts_at} className="mt-4" />

      <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <Clock className="h-4 w-4" /> {event.duration_minutes} min
        </span>
        <span className="inline-flex items-center gap-1.5">
          <CalendarDays className="h-4 w-4" /> Live online (Zoom)
        </span>
        <span>Hosted by {event.host}</span>
      </div>


      <img
        loading="lazy"
        decoding="async"
        src={eventArtwork(event)}
        alt={eventArtworkAlt(event)}
        width={1280}
        height={800}
        className="mt-6 h-36 w-full rounded-2xl border border-gold/30 object-cover object-[50%_38%] sm:h-48 md:h-56"
      />



      <p className="mt-6 whitespace-pre-wrap text-base leading-relaxed text-foreground">
        {event.description}
      </p>

      <section className="mt-8 rounded-2xl border border-gold/40 bg-cream/40 p-6">
        {canReserve && !isPastEvent ? (
          <div className="mb-3 inline-flex items-center gap-1.5 rounded-full bg-[var(--gold-deep)] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-white">
            <Lock className="h-3 w-3" /> Members-only registration
          </div>
        ) : null}
        {(() => {
          if (isPastEvent) {
            return (
              <div>
                <p className="font-display text-xl text-foreground sm:text-2xl">
                  This event has already taken place
                </p>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  Booking is closed for this session. You're very welcome to join one of our
                  upcoming live workshops whenever it feels right for you.
                </p>
                <Link
                  to="/events"
                  className="btn-gold-outline mt-5 w-full justify-center text-sm sm:w-auto"
                >
                  See Upcoming Events
                </Link>
              </div>
            );
          }
          if (!canReserve) {

            return (
              <div>
                <p className="font-display text-xl text-foreground sm:text-2xl">
                  Want to join this live workshop?
                </p>
                <p className="mt-3 text-sm leading-relaxed text-foreground/85">
                  Live workshops are included with Recovery Trauma™ membership.
                </p>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  Become a member to join our live sessions and access the wider Recovery Trauma™
                  recovery platform.
                </p>
                <Link
                  to={MEMBERSHIP_JOURNEY_PATH}
                  onClick={() => startMembershipJourney("event_detail_join", "event_detail")}
                  className="btn-gold mt-5 w-full justify-center text-sm sm:w-auto"
                >
                  <Ticket className="h-4 w-4" />
                  Join Recovery Trauma™
                </Link>
                <div className="mt-6 border-t border-gold/25 pt-5">
                  <p className="text-sm text-muted-foreground">
                    Not ready to join? You can continue exploring Recovery Trauma™ first.
                  </p>
                  <Link
                    to="/explore"
                    className="btn-gold-outline mt-3 w-full justify-center text-sm sm:w-auto"
                  >
                    Explore Recovery Trauma™
                  </Link>
                </div>
              </div>
            );
          }

          return (
            <div className="flex flex-col gap-3">
              <WorkshopJoinPanel
                startsAt={event.starts_at}
                durationMinutes={event.duration_minutes ?? 60}
                zoomUrl={zoomUrl}
                zoomPasscode={zoomPasscode}
                eventTitle={event.title}
              />

              <button
                type="button"
                onClick={() => reserveMutation.mutate()}
                disabled={reserveMutation.isPending}
                className={`w-full justify-center text-sm ${
                  reserved ? "btn-gold-outline" : "btn-gold"
                }`}
              >
                <Ticket className="h-4 w-4" />
                {reserveMutation.isPending
                  ? "Saving…"
                  : reserved
                    ? "Cancel Reservation"
                    : eventReserveLabel(slug)}
              </button>

              {reserved ? (
                <div className="rounded-2xl border border-gold/30 bg-gold-light/20 p-4">
                  <p className="font-display text-base text-foreground">
                    {eventBookedMessage(slug).title}
                  </p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {eventBookedMessage(slug).note ??
                      formatEventDualTime(event.starts_at, detectTimeZone())}
                  </p>
                </div>
              ) : null}


              <p className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                <Users className="h-3.5 w-3.5" />
                {event.reservation_count === 0
                  ? "No places reserved yet — you can be the first"
                  : `${event.reservation_count} ${
                      event.reservation_count === 1 ? "place" : "places"
                    } reserved${
                      event.registration_limit
                        ? ` of ${event.registration_limit}`
                        : ""
                    }`}
              </p>
            </div>
          );
        })()}


        {canReserve && !isPastEvent ? (

          <p className="mt-3 text-xs text-muted-foreground">
            You'll get a 24-hour and a 1-hour reminder by email if you reserve a place.
          </p>
        ) : null}
      </section>

      <div className="mt-8">
        <ComplianceNotice variant="events" />
      </div>

      {canReserve ? (
        <>
          <EventFeedback slug={slug} eventTitle={event.title} />
          <DiscussionThread slug={slug} title={event.title} />
        </>
      ) : (
        <section className="mt-10 rounded-2xl border border-dashed border-gold/40 bg-cream/30 p-6 text-center">
          <p className="text-sm text-foreground">
            💬 Member discussion and post-event feedback unlock with an active Membership.
          </p>
        </section>
      )}
    </div>
  );
}

function DiscussionThread({ slug, title }: { slug: string; title: string }) {
  const [messages, setMessages] = useState<DiscussionMsg[]>([]);
  const [name, setName] = useState("");
  const [body, setBody] = useState("");

  useEffect(() => {
    setMessages(getDiscussion(slug));
  }, [slug]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!body.trim()) return;
    postDiscussion(slug, { name: name.trim() || "Anonymous member", body: body.trim() });
    setMessages(getDiscussion(slug));
    setBody("");
  };

  return (
    <section className="mt-10">
      <h2 className="font-display text-xl text-foreground">
        <MessageCircle className="me-2 inline h-5 w-5 text-[var(--gold-deep)]" />
        💬 {title} Discussion
      </h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Ask questions, share reflections, and continue the conversation after the event.
      </p>

      <form onSubmit={submit} className="mt-4 rounded-2xl border border-border/50 bg-card p-4">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your name (optional)"
          className="w-full rounded-lg border border-border/60 bg-background px-3 py-2 text-sm outline-none focus:border-gold/50"
        />
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Share a reflection or question…"
          rows={3}
          className="mt-2 w-full rounded-lg border border-border/60 bg-background px-3 py-2 text-sm outline-none focus:border-gold/50"
        />
        <div className="mt-2 flex justify-end">
          <button type="submit" className="btn-gold text-sm" disabled={!body.trim()}>
            <Send className="h-4 w-4" /> Post
          </button>
        </div>
      </form>

      <ul className="mt-4 space-y-3">
        {messages.length === 0 && (
          <li className="rounded-xl border border-dashed border-border/50 bg-cream/40 p-6 text-center text-sm text-muted-foreground">
            Be the first to share a reflection.
          </li>
        )}
        {messages
          .slice()
          .reverse()
          .map((m) => (
            <li key={m.id} className="rounded-xl border border-border/50 bg-card p-4">
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span className="font-semibold text-foreground">{m.name}</span>
                <time>{new Date(m.at).toLocaleString()}</time>
              </div>
              <p className="mt-2 whitespace-pre-wrap text-sm text-foreground">{m.body}</p>
            </li>
          ))}
      </ul>
    </section>
  );
}
